import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Opt} from 'ts-opt';

import {AnimalList} from 'api/gen/AnimalList';
import {AssociationType} from 'api/gen/AssociationType';
import {AvailableAnimal} from 'api/gen/AvailableAnimal';
import {Bookmark} from 'api/gen/Bookmark';
import {CreateOrUpdateGridUserSettings} from 'api/gen/CreateOrUpdateGridUserSettings';
import {CriteriaDefinition} from 'api/gen/CriteriaDefinition';
import {CriteriaList} from 'api/gen/CriteriaList';
import {Sex} from 'api/gen/Sex';
import {WriteCriteria} from 'api/gen/WriteCriteria';
import {matingAction, matingSelector} from 'app/mating/model';
import {State} from 'app/setup';

interface UseAnimalModalsProps {
    /** Association type that drives the modal actions. */
    associationType: AssociationType;
    /** Criteria definitions used by the create-criteria modal. */
    criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
    /** Sex-specific modal mode. */
    sex: Sex;
}

interface UseAnimalModalsResult {
    /** Current saved animal list id selected for confirmation dialogs. */
    animalListId: Opt<number>;
    /** Saved animal lists available to the user. */
    animalLists: Opt<Array<AnimalList>>;
    /** Animals available for the add-animal modal. */
    availableAnimals: Opt<Array<AvailableAnimal>>;
    /** Current saved criteria list id selected for confirmation dialogs. */
    criteriaListId: Opt<number>;
    /** Saved criteria lists available to the user. */
    criteriaLists: Opt<Array<CriteriaList>>;
    /** Loading flags relevant to the modal actions. */
    loading: {
        /** Whether saved animal lists are loading. */
        animalLists: boolean,
        /** Whether available animals are loading. */
        availableAnimals: boolean,
        /** Whether the animal-list name confirmation is submitting. */
        confirmAnimalListName: boolean,
        /** Whether the criteria-list name confirmation is submitting. */
        confirmCriteriaListName: boolean,
    };
    /** Visibility flags for each animal-related modal. */
    visibility: {
        /** Whether the add-animals modal is visible. */
        addAnimals: boolean,
        /** Whether the add-from-DB modal is visible. */
        addAnimalsFromDb: boolean,
        /** Whether the animal-lists modal is visible. */
        animalLists: boolean,
        /** Whether the animal-list confirmation modal is visible. */
        animalListsConfirm: boolean,
        /** Whether the criteria-list-details modal is visible. */
        criteriaListDetails: boolean,
        /** Whether the criteria-lists modal is visible. */
        criteriaLists: boolean,
        /** Whether the criteria-list confirmation modal is visible. */
        criteriaListsConfirm: boolean,
        /** Whether the saved-lists overview modal is visible. */
        listsOverview: boolean,
    };
    /** Modal action handlers grouped by workflow. */
    actions: {
        /** Adds animals from the current modal selection. */
        addAnimals(animalIds: Array<number>): void,
        /** Adds animals from a saved list. */
        addAnimalsFromDb(listId: number): void,
        /** Confirms the selected animal list. */
        confirmAnimalList(listId: number): void,
        /** Confirms the selected criteria list and saves its data. */
        confirmCriteriaList(
            criteriaList: Array<WriteCriteria>,
            gridUserSettings: CreateOrUpdateGridUserSettings | null,
        ): void,
        /** Creates a new animal list. */
        createAnimalList(name: string): void,
        /** Creates a new criteria list. */
        createCriteriaList(name: string): void,
        /** Deletes an animal list. */
        deleteAnimalList(listId: number): void,
        /** Deletes a criteria list. */
        deleteCriteriaList(listId: number, gridUserSettingsId: number | null): void,
        /** Exports the current animals table to CSV. */
        getAnimalsCsv(fileName: string): void,
        /** Loads animals for an autocomplete input. */
        getAvailableAnimals(input: string): void,
        /** Loads criteria-list details. */
        getCriteriaListDetails(listId: number): void,
        /** Renames an animal list. */
        renameAnimalList(id: number, name: string): void,
        /** Renames a criteria list. */
        renameCriteriaList(id: number, name: string, gridUserSettingsId: number | null): void,
        /** Marks the available-animals query as loading. */
        setAvailableAnimalsLoading(): void,
        /** Toggles overwrite visibility for an animal list. */
        toggleOverwriteAnimalListVisibility(listId: Opt<number>): void,
        /** Toggles overwrite visibility for a criteria list. */
        toggleOverwriteCriteriaListVisibility(
            gridUserSettings: CreateOrUpdateGridUserSettings | null,
            writeCriteria?: Array<WriteCriteria>,
        ): void,
    };
}

interface VisibilitySelectors {
    addAnimals(state: State): boolean;
    addAnimalsFromDb(state: State): boolean;
    animalLists(state: State): boolean;
    animalListsConfirm(state: State): boolean;
    criteriaListDetails(state: State): boolean;
    criteriaLists(state: State): boolean;
    criteriaListsConfirm(state: State): boolean;
    listsOverview(state: State): boolean;
}

const getVisibilitySelector = (sex: Sex): VisibilitySelectors => ({
    addAnimals: sex === 'FEMALE' ? matingSelector.addCowsModalVisible : matingSelector.addBullsModalVisible,
    addAnimalsFromDb: sex === 'FEMALE'
        ? matingSelector.cowsFromDBVisibilityModalVisible
        : matingSelector.bullsFromDBVisibilityModalVisible,
    animalLists: sex === 'FEMALE'
        ? matingSelector.femaleAnimalListsModalVisible
        : matingSelector.maleAnimalListsModalVisible,
    animalListsConfirm: sex === 'FEMALE'
        ? matingSelector.femaleAnimalListsConfirmModalVisible
        : matingSelector.maleAnimalListsConfirmModalVisible,
    criteriaListDetails: sex === 'FEMALE'
        ? matingSelector.cowsCriteriaListDetailsModalVisible
        : matingSelector.bullsCriteriaListDetailsModalVisible,
    criteriaLists: sex === 'FEMALE'
        ? matingSelector.cowsCriteriaListsModalVisible
        : matingSelector.bullsCriteriaListsModalVisible,
    criteriaListsConfirm: sex === 'FEMALE'
        ? matingSelector.cowsCriteriaListsConfirmModalVisible
        : matingSelector.bullsCriteriaListsConfirmModalVisible,
    listsOverview: sex === 'FEMALE'
        ? matingSelector.cowsListsOverviewModalVisible
        : matingSelector.bullsListsOverviewModalVisible,
});

/**
 * Builds the modal actions and visibility state for a mating animal tab.
 *
 * @param props Association type, criteria definitions, and sex-specific mode.
 * @returns Modal state and handlers for the current mating workflow.
 */
// eslint-disable-next-line max-lines-per-function
export const useAnimalModals = (_: UseAnimalModalsProps): UseAnimalModalsResult => {
    const {associationType, criteriaDefinitions, sex} = _;
    const dispatch = useDispatch();
    const animalListId = useSelector(matingSelector.animalListId);
    const animalLists = useSelector(matingSelector.animalLists);
    const availableAnimals = useSelector(matingSelector.availableAnimals);
    const criteriaListId = useSelector(matingSelector.criteriaListId);
    const criteriaLists = useSelector(matingSelector.criteriaLists);
    const loading = useSelector(matingSelector.loading);

    const bookmark: Bookmark = sex;
    const visibilitySelector = getVisibilitySelector(sex);
    const visibility = {
        addAnimals: useSelector(visibilitySelector.addAnimals),
        addAnimalsFromDb: useSelector(visibilitySelector.addAnimalsFromDb),
        animalLists: useSelector(visibilitySelector.animalLists),
        animalListsConfirm: useSelector(visibilitySelector.animalListsConfirm),
        criteriaListDetails: useSelector(visibilitySelector.criteriaListDetails),
        criteriaLists: useSelector(visibilitySelector.criteriaLists),
        criteriaListsConfirm: useSelector(visibilitySelector.criteriaListsConfirm),
        listsOverview: useSelector(visibilitySelector.listsOverview),
    };

    const actions = {
        addAnimals: useCallback((animalIds: Array<number>): void => {
            dispatch(matingAction.addAnimals(sex, animalIds, associationType));
        }, [associationType, dispatch, sex]),
        addAnimalsFromDb: useCallback((listId: number): void => {
            dispatch(matingAction.addAnimalsFromDB(sex, listId, associationType));
        }, [associationType, dispatch, sex]),
        confirmAnimalList: useCallback((listId: number): void => {
            dispatch(matingAction.confirmAnimalList(sex, listId, associationType));
        }, [associationType, dispatch, sex]),
        confirmCriteriaList: useCallback((
            criteriaList: Array<WriteCriteria>,
            gridUserSettings: CreateOrUpdateGridUserSettings | null,
        ): void => {
            dispatch(matingAction.confirmCriteriaList({
                bookmark,
                criteriaListId: criteriaListId.orCrash('criteriaListId not available'),
                criteria: criteriaList,
                gridUserSettings,
                associationType,
            }));
        }, [associationType, bookmark, criteriaListId, dispatch]),
        createAnimalList: useCallback((name: string): void => {
            dispatch(matingAction.createAnimalList(sex, name, associationType));
        }, [associationType, dispatch, sex]),
        createCriteriaList: useCallback((name: string): void => {
            dispatch(matingAction.createCriteriaList(bookmark, name, criteriaDefinitions, associationType));
        }, [associationType, bookmark, criteriaDefinitions, dispatch]),
        deleteAnimalList: useCallback((listId: number): void => {
            dispatch(matingAction.deleteAnimalList(listId, sex, associationType));
        }, [associationType, dispatch, sex]),
        deleteCriteriaList: useCallback((listId: number, gridUserSettingsId: number | null): void => {
            dispatch(matingAction.deleteCriteriaList(listId, bookmark, associationType, gridUserSettingsId));
        }, [associationType, bookmark, dispatch]),
        getAnimalsCsv: useCallback((fileName: string): void => {
            dispatch(matingAction.getAnimalsCsv(sex, fileName, associationType));
        }, [associationType, dispatch, sex]),
        getAvailableAnimals: useCallback((input: string): void => {
            dispatch(matingAction.getAvailableAnimals(sex, input, associationType));
        }, [associationType, dispatch, sex]),
        getCriteriaListDetails: useCallback((listId: number): void => {
            dispatch(matingAction.getCriteriaListDetails(bookmark, associationType, listId));
        }, [associationType, bookmark, dispatch]),
        renameAnimalList: useCallback((id: number, name: string): void => {
            dispatch(matingAction.renameAnimalList(sex, id, name, associationType));
        }, [associationType, dispatch, sex]),
        renameCriteriaList: useCallback((id: number, name: string, gridUserSettingsId: number | null): void => {
            dispatch(matingAction.renameCriteriaList({
                bookmark,
                id,
                gridUserSettingsId,
                name,
                associationType,
            }));
        }, [associationType, bookmark, dispatch]),
        setAvailableAnimalsLoading: useCallback((): void => {
            dispatch(matingAction.setLoading('getAvailableAnimals', true));
        }, [dispatch]),
        toggleOverwriteAnimalListVisibility: useCallback((listId: Opt<number>): void => {
            dispatch(matingAction.toggleOverwriteAnimalListVisibility(sex, listId));
        }, [dispatch, sex]),
        toggleOverwriteCriteriaListVisibility: useCallback((
            gridUserSettings: CreateOrUpdateGridUserSettings | null,
            writeCriteria?: Array<WriteCriteria>,
        ): void => {
            dispatch(matingAction.toggleOverwriteCriteriaListVisibility(bookmark, gridUserSettings, writeCriteria));
        }, [bookmark, dispatch]),
    };

    return {
        animalListId,
        animalLists,
        availableAnimals,
        criteriaListId,
        criteriaLists,
        loading: {
            animalLists: loading.includes('animalLists'),
            availableAnimals: loading.includes('getAvailableAnimals'),
            confirmAnimalListName: loading.includes('confirmAnimalListNameInProgress'),
            confirmCriteriaListName: loading.includes('confirmCriteriaListNameInProgress'),
        },
        visibility,
        actions,
    };
};
